import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  format,
  startOfWeek,
} from "date-fns";
import { cn } from "@/lib/utils";

type RangePreset = "week" | "2weeks" | "month";

interface TimelineNavigationProps {
  startDate: Date;
  endDate: Date;
  onRangeChange: (start: Date, end: Date) => void;
}

function rangeFor(preset: RangePreset): { start: Date; end: Date } {
  const start = startOfWeek(new Date(), { weekStartsOn: 1 });
  switch (preset) {
    case "week":
      return { start, end: addDays(start, 6) };
    case "2weeks":
      return { start, end: addDays(start, 13) };
    case "month":
      return { start, end: endOfMonth(new Date()) };
  }
}

/** Date-range navigator above the timeline: prev / next shift by the
 *  visible span, Today re-centers on the current preset, and the 1W / 2W /
 *  1M presets resize the window. */
export function TimelineNavigation({
  startDate,
  endDate,
  onRangeChange,
}: TimelineNavigationProps) {
  const [preset, setPreset] = useState<RangePreset>("2weeks");

  // Inclusive span of the current window, used to step prev/next.
  const span = differenceInCalendarDays(endDate, startDate) + 1;

  function applyPreset(next: RangePreset) {
    setPreset(next);
    const { start, end } = rangeFor(next);
    onRangeChange(start, end);
  }

  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous period"
          onClick={() =>
            onRangeChange(addDays(startDate, -span), addDays(endDate, -span))
          }
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[190px] text-center text-sm font-medium text-slate-700">
          {format(startDate, "d MMM")} to {format(endDate, "d MMM yyyy")}
        </span>
        <button
          type="button"
          aria-label="Next period"
          onClick={() =>
            onRangeChange(addDays(startDate, span), addDays(endDate, span))
          }
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => applyPreset(preset)}
          className="ml-1 text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-700"
        >
          Today
        </button>
      </div>

      <div className="inline-flex h-9 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
        {(["week", "2weeks", "month"] as const).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => applyPreset(r)}
            aria-pressed={preset === r}
            className={cn(
              "inline-flex h-8 items-center rounded-md px-2.5 text-xs font-medium transition-colors",
              preset === r
                ? "bg-white text-slate-900 shadow-sm-tile"
                : "text-slate-500 hover:text-slate-700",
            )}
          >
            {r === "week" ? "1W" : r === "2weeks" ? "2W" : "1M"}
          </button>
        ))}
      </div>
    </div>
  );
}
